import { captureException } from '@sentry/react';
import { cancellationSurveyReasonCodes } from './types';
import type { CancellationReason, CancellationSurveyResponse } from './types';

// California users answer the survey on the billing page before being
// redirected to the cancellation page, which sends the answer with the
// cancellation. Session storage keeps the free-text context out of the URL.
const storageKey = 'sign.cancellationSurveyResponse';

const reasonCodes: readonly string[] = Object.values(
  cancellationSurveyReasonCodes,
);

function isCancellationReason(value: unknown): value is CancellationReason {
  return typeof value === 'string' && reasonCodes.includes(value);
}

// Storage access throws when the browser blocks it; the survey answer is
// optional, so cancellation continues without it.
export function savePendingSurveyResponse(
  response: CancellationSurveyResponse,
) {
  if (!response.reason) {
    return;
  }
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(response));
  } catch (error) {
    captureException(error);
  }
}

export function readPendingSurveyResponse():
  | CancellationSurveyResponse
  | undefined {
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (!stored) {
      return undefined;
    }
    const { reason, context } = JSON.parse(stored);
    if (!isCancellationReason(reason)) {
      return undefined;
    }
    return { reason, context: typeof context === 'string' ? context : '' };
  } catch {
    return undefined;
  }
}

export function clearPendingSurveyResponse() {
  try {
    window.sessionStorage.removeItem(storageKey);
  } catch {
    // Ignored, see above.
  }
}
